import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, FileText, ExternalLink, ChevronRight, FilePlus2 } from 'lucide-react';
import { usd, fmtNum, StatusChip, openAuthed } from '../lib/q2c';
import { countWord } from '../lib/countWord.mjs';
import { safeHref } from '../../modules/copilot/safe-href.mjs';

/**
 * Copilot chat-panel cards (LP-394).
 *
 * QuoteCard: the quote as the customer will see it (lines, totals, $/sf all-in), who it goes to
 * (customer, attention, recipient email), what the last edit changed (each before → after), Preview / PDF /
 * Open buttons, and a collapsed INTERNAL "Basis for pricing" panel (costs and where they came
 * from, landed freight, margins, comparables, the cost-plus floor, assumptions, open questions).
 * The basis is partner-only data from the chat answer; the customer view and PDF never carry it.
 *
 * (The LP-394 RfpCard, which offered the retired "RFP to Concria" text to copy, is removed: Concria is
 * asked for prices only with the supplier RFQ, LP-397.)
 *
 * DraftLink: any other draft (a task, content, a supplier RFQ). The answer's hrefs are model-influenced,
 * so a draft's href and a card's editor link are links only when safeHref (modules/copilot/safe-href.mjs)
 * says they stay on this origin; otherwise the label shows as plain text (LP-443). A quote card's Preview and
 * PDF open only a safeHref result too; with any other link the button is off (LP-442).
 */

// Unit prices and costs keep up to 4 decimals ($0.2526/sf), as stored.
const unitPrice = (l) => {
  const p = `$${Number(l.unit_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  if (l.pricing === 'per_sf' || l.unit === 'sf') return `${p}/sf`;
  return l.unit && l.unit !== 'lot' && l.pricing !== 'lump' ? `${p} per ${l.unit}` : p;
};
const qtyText = (l) => (l.pricing === 'lump' ? 'lump'
  : `${fmtNum(l.qty)} ${countWord(l.qty, l.unit)}`.trim());
const pct = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
const perSf = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}/sf`);

const OFFER = {
  budgetary: { label: 'Budgetary', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  firm: { label: 'Firm', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200' },
};

function Section({ title, children }) {
  return (
    <div className="mt-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</p>
      <div className="mt-0.5 space-y-1">{children}</div>
    </div>
  );
}

function BasisPanel({ b }) {
  if (!b) return null;
  const t = b.totals || {};
  const f = b.freight;
  return (
    <details className="group border-t border-gray-100 px-3 py-2 dark:border-slate-700">
      <summary className="flex cursor-pointer list-none items-center gap-1 text-xs font-semibold text-gray-700 dark:text-gray-200">
        <ChevronRight size={13} className="transition-transform group-open:rotate-90" />
        Basis for pricing
        <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:bg-slate-800 dark:text-gray-400">internal</span>
      </summary>
      <div className="mt-1 text-[11px] leading-snug text-gray-700 dark:text-gray-300">
        <p>
          {b.method === 'cost_plus' ? 'Cost-plus pricing.' : 'Priced from comparable quotes.'} Cost {usd(t.cost_total)}, margin {pct(t.margin_pct)}.
          {t.cost_plus_floor && <> Cost-plus floor {usd(t.cost_plus_floor.total)} ({perSf(t.cost_plus_floor.per_sf)}).</>}
          {t.cost_plus_floor_kb_rule && <> At the KB rule: {usd(t.cost_plus_floor_kb_rule.total)} ({perSf(t.cost_plus_floor_kb_rule.per_sf)}).</>}
        </p>

        <Section title="Lines">
          {(b.lines || []).map((l) => (
            <div key={l.id} className="rounded border border-gray-100 p-1.5 dark:border-slate-700">
              <p className="font-medium text-gray-800 dark:text-gray-100">{l.label}{l.optional ? ' (optional)' : ''}</p>
              {l.price_source && <p>Price: {l.price_source}</p>}
              <p>
                Cost: {l.unit_cost != null ? `${unitPrice({ ...l, unit_price: l.unit_cost })} = ${usd(l.cost_amount)}` : 'unknown'}
                {l.margin_pct != null && <>, margin {pct(l.margin_pct)}</>}
                {l.floor_unit_price != null && <>; floor {unitPrice({ ...l, unit_price: l.floor_unit_price })} ({l.floor_rule})</>}
              </p>
              {l.cost_source && <p className="text-gray-500 dark:text-gray-400">From: {l.cost_source}</p>}
              {l.alt_cost && (
                <p className="text-gray-500 dark:text-gray-400">
                  Or {l.alt_cost.label}: {l.alt_cost.unit_cost != null ? `${unitPrice({ ...l, unit_price: l.alt_cost.unit_cost })} = ${usd(l.alt_cost.cost_amount)}, margin ${pct(l.alt_cost.margin_pct)}` : l.alt_cost.note}
                </p>
              )}
              {(l.checks || []).map((c, i) => <p key={i} className="text-amber-700 dark:text-amber-300">{c}</p>)}
            </div>
          ))}
        </Section>

        {f && f.source === 'estimate' && (
          <Section title="Landed freight">
            <p>{f.loads} load{f.loads === 1 ? '' : 's'} x {usd(f.per_load_usd)} ({f.estimate_name}); {fmtNum(f.shipped_lb)} lb shipped{f.per_lb != null ? `, $${Number(f.per_lb).toFixed(4)}/lb` : ''}{f.per_sf != null ? `, ${perSf(f.per_sf)}` : ''}.</p>
            {(f.charges || []).map((c, i) => <p key={i}>{c.label}: {usd(c.usd)}</p>)}
          </Section>
        )}

        {(b.comparables || []).length > 0 && (
          <Section title="Comparables">
            {b.comparables.map((m) => (
              <p key={m.number}>
                {m.named ? '★ ' : ''}{m.customer || m.title} {m.number} ({m.status}): {perSf(m.all_in_per_sf)} all-in{m.all_in_per_lb != null ? `, $${Number(m.all_in_per_lb).toFixed(2)}/lb` : ''}, {fmtNum(m.area_sf)} sf
              </p>
            ))}
          </Section>
        )}

        {(b.checks || []).length > 0 && <Section title="Checks">{b.checks.map((c, i) => <p key={i} className={c.severity === 'block' ? 'text-red-700 dark:text-red-300' : ''}>{c.message}</p>)}</Section>}
        {(b.assumptions || []).length > 0 && <Section title="Assumptions">{b.assumptions.map((a, i) => <p key={i}>{a}</p>)}</Section>}
        {(b.open_questions || []).length > 0 && <Section title="Open questions">{b.open_questions.map((a, i) => <p key={i}>{a}</p>)}</Section>}
      </div>
    </details>
  );
}

export function QuoteCard({ q, onNavigate }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  // The document links are model-influenced too: openAuthed fetches only a safeHref result (LP-442, #59 gate).
  const docs = { preview: safeHref(q.links?.preview), pdf: safeHref(q.links?.pdf) };
  const open = async (kind) => {
    const to = docs[kind];
    if (!to) return;
    setBusy(kind); setErr('');
    try { await openAuthed(to); } catch (e) { setErr(e.message); } finally { setBusy(null); }
  };
  const offer = OFFER[q.offer_type];
  const editor = safeHref(q.links?.editor);
  const btn = 'flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium hover:border-primary hover:text-primary disabled:opacity-50 dark:border-slate-600';
  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white text-gray-800 dark:border-slate-600 dark:bg-slate-900 dark:text-gray-100">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-100 px-3 py-2 dark:border-slate-700">
        <span className="text-xs font-semibold">{q.number} V{q.version}</span>
        <StatusChip status={q.status} />
        {offer && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${offer.cls}`}>{offer.label}</span>}
        {(q.customer || q.recipient || q.recipient_email) && <span className="w-full truncate text-[11px] text-gray-500 dark:text-gray-400">{[q.customer, q.recipient && `Attn ${q.recipient}`, q.recipient_email].filter(Boolean).join(' · ')}</span>}
      </div>
      {q.changes?.length > 0 && (
        <div className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-[11px] dark:border-slate-700 dark:bg-slate-800/60">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Changed</p>
          {q.changes.map((c, i) => <p key={i} className="break-words">{c}</p>)}
        </div>
      )}
      <div className="px-3 py-2 text-[11px]">
        {q.lines.map((l) => (
          <div key={l.id} className={`flex items-start justify-between gap-2 py-0.5 ${l.optional ? 'text-gray-400' : ''}`}>
            <div className="min-w-0">
              <p className="break-words">{l.description}{l.optional ? ' (optional, not in total)' : ''}</p>
              <p className="text-gray-500 dark:text-gray-400">{qtyText(l)}{l.pricing !== 'lump' ? ` at ${unitPrice({ ...l, unit: null })}` : ''}{l.bags ? ` · ${fmtNum(l.bags)} ${countWord(l.bags, 'bag')}` : ''}</p>
            </div>
            <span className="shrink-0 font-medium tabular-nums">{usd(l.amount)}</span>
          </div>
        ))}
        <div className="mt-1 flex items-baseline justify-between border-t border-gray-100 pt-1 dark:border-slate-700">
          <span className="font-semibold">Total{q.totals.tax ? '' : ' (excl. tax)'}</span>
          <span className="text-sm font-semibold tabular-nums">{usd(q.totals.total)}</span>
        </div>
        {q.totals.per_sf_all_in != null && <p className="text-right text-gray-500 dark:text-gray-400">{perSf(q.totals.per_sf_all_in)} all-in over {fmtNum(q.totals.area_sf)} sf</p>}
        {q.ready_for_approval
          ? <p className="mt-1 rounded bg-indigo-50 px-2 py-1 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-200">Ready for approval. {q.approvers?.rule} A partner approves and sends it in Quotes.</p>
          : q.status === 'draft' && <p className="mt-1 text-gray-500 dark:text-gray-400">Draft, not sent. {q.approvers?.rule}</p>}
        <div className="mt-2 flex flex-wrap gap-1.5">
          <button type="button" className={btn} disabled={!docs.preview || busy === 'preview'} onClick={() => open('preview')}><Eye size={12} /> Preview</button>
          <button type="button" className={btn} disabled={!docs.pdf || busy === 'pdf'} onClick={() => open('pdf')}><FileText size={12} /> PDF</button>
          {editor
            ? <Link to={editor} onClick={onNavigate} className={btn}><ExternalLink size={12} /> Open in Quotes</Link>
            : <span className="px-2 py-1 text-[11px] text-gray-500 dark:text-gray-400">Open in Quotes</span>}
        </div>
        {err && <p className="mt-1 text-red-600">{err}</p>}
      </div>
      <BasisPanel b={q.basis} />
    </div>
  );
}

const draftCls = 'flex items-center gap-1.5 rounded-lg border border-primary/30 bg-white px-2 py-1.5 text-xs font-medium dark:bg-slate-900';

/** A draft the Copilot made: a link to it when its href is a same-origin portal path, else its label as text. */
export function DraftLink({ d, onNavigate }) {
  const to = safeHref(d.href);
  if (!to) return <p className={`${draftCls} text-gray-700 dark:text-gray-200`}><FilePlus2 size={13} /> {d.label}</p>;
  return <Link to={to} onClick={onNavigate} className={`${draftCls} text-primary`}><FilePlus2 size={13} /> {d.label}</Link>;
}
